import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import dynamoService from '../services/dynamoService';
import './BlockedMessages.css';

const BlockedMessages = () => {
  const { isDarkMode } = useTheme();
  const [blockedMessages, setBlockedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullText, setShowFullText] = useState({});

  useEffect(() => {
    loadBlockedMessages(true);
    
    // Set up auto-refresh every 15 seconds
    const interval = setInterval(() => {
      loadBlockedMessages(false); // Don't show loading for auto-refresh
    }, 15000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const loadBlockedMessages = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const result = await dynamoService.getBlockedMessages();
      
      if (result.success) {
        // Sort messages from most recent to oldest
        const sortedMessages = result.items.sort((a, b) => {
          const timeA = new Date(a.blockedAt || a.timestamp || 0).getTime();
          const timeB = new Date(b.blockedAt || b.timestamp || 0).getTime();
          return timeB - timeA; // Descending order (recent first)
        });
        setBlockedMessages(sortedMessages);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Failed to load blocked messages:', err);
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const maskEmail = (email) => {
    if (!email) return 'N/A';
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;
    
    const maskedLocal = localPart.length > 2 
      ? localPart.substring(0, 2) + '*'.repeat(localPart.length - 2)
      : localPart;
    
    return `${maskedLocal}@${domain}`;
  };

  const truncateText = (text, messageId) => {
    if (!text) return 'N/A';
    
    if (showFullText[messageId]) {
      return text;
    }
    
    if (text.length <= 10) {
      return text.substring(0, 2) + (text.length > 2 ? '...' : '');
    } else {
      return text.substring(0, 2) + '...' + text.substring(text.length - 2);
    }
  };

  const toggleTextDisplay = (messageId) => {
    setShowFullText(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle both Unix timestamp (number) and ISO string
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };


  if (loading) {
    return (
      <div className={`blocked-messages ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading blocked messages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`blocked-messages ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="error-container">
          <h2>Error Loading Blocked Messages</h2>
          <p className="error-message">{error}</p>
          <button onClick={() => loadBlockedMessages(true)} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`blocked-messages ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="blocked-header">
        <h1>Blocked Messages</h1>
        <p className="subtitle">Messages that were blocked by AWS Bedrock Guardrails</p>
        
        
        <div className="stats">
          <span className="stat-item">
            <strong>{blockedMessages.length}</strong> blocked messages
          </span>
          <button onClick={() => loadBlockedMessages(true)} className="refresh-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', verticalAlign: 'middle'}}>
              <polyline points="23,4 23,10 17,10"></polyline>
              <polyline points="1,20 1,14 7,14"></polyline>
              <path d="m3.51,9a9,9,0,0,1,14.85-3.36L23,10M1,14l4.64,4.36A9,9,0,0,0,20.49,15"></path>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {blockedMessages.length === 0 ? (
        <div className="no-messages">
          <p>No blocked messages found.</p>
        </div>
      ) : (
        <div className="messages-table-container">
          <table className="messages-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Text</th>
                <th>Blocked At</th>
              </tr>
            </thead>
            <tbody>
              {blockedMessages.map((message, index) => (
                <tr key={message.messageId || index}>
                  <td className="email" data-label="Email">{maskEmail(message.email)}</td>
                  <td className="text-cell" data-label="Text">
                    <span className="text">{truncateText(message.text, message.messageId || index)}</span>
                    {message.text && message.text.length > 2 && (
                      <button 
                        className="show-text-toggle"
                        onClick={() => toggleTextDisplay(message.messageId || index)}
                        aria-label={showFullText[message.messageId || index] ? "Hide full text" : "Show full text"}
                        title={showFullText[message.messageId || index] ? "Hide full text" : "Show full text"}
                      >
                        {showFullText[message.messageId || index] ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m15,18-.32-.32c1.33-.85,2.48-2.05,3.49-3.68l-6.17-6.17V12"></path>
                            <path d="M20.83,16.77c.19-.4,.36-.8,.5-1.21"></path>
                            <path d="M9.68,5.68c1.65-.87,3.32-1.31,5-1.31c1.76,0,3.49,.48,5.18,1.44"></path>
                            <path d="M1,1l22,22"></path>
                            <path d="m2,12l5.4,5.4c.81,.81,1.97,1.28,3.14,1.28s2.33-.47,3.14-1.28l.32-.32"></path>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="timestamp" data-label="Blocked At">{formatTimestamp(message.blockedAt || message.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlockedMessages;
